import React, { useState, useMemo } from "react";
import {
  UsersIcon,
  XIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from "@heroicons/react/outline";

interface BidderUser {
  user_uuid: string;
  event_uuid: string;
  bidder_name: string;
  bidder_email: string;
  total_session_duration_ms: number;
  total_connections: number;
  first_connection: string;
  last_disconnection: string;
}

interface BidderSessionData {
  event_uuid: string;
  event_name: string;
  total_users: number;
  total_sessions: number;
  users: BidderUser[];
}

interface UserTypeConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  iconColor: string;
  badgeColor: string;
  bgColor: string;
}

interface FloatingUserHistoryProps {
  bidderSessionData: BidderSessionData;
  position?: "left" | "right";
  initiallyOpen?: boolean;
}

const FloatingUserHistory: React.FC<FloatingUserHistoryProps> = ({
  bidderSessionData,
  position = "right",
  initiallyOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    { bidder: true }
  );

  // Filter bidders from bidderSessionData.users
  const filteredBidders = useMemo(() => {
    if (!bidderSessionData?.users) return [];

    return bidderSessionData.users
      .filter(
        (user: BidderUser) =>
          user.bidder_name &&
          user.bidder_name !== "NA" &&
          user.bidder_email &&
          user.bidder_email !== "NA"
      )
      .map((user: BidderUser) => ({
        id: user.user_uuid,
        name: user.bidder_name,
        email: user.bidder_email,
        user_type: "bidder" as const,
        sessionDuration: user.total_session_duration_ms,
        totalConnections: user.total_connections,
        firstConnection: user.first_connection,
        lastDisconnection: user.last_disconnection,
      }));
  }, [bidderSessionData]);

  const totalBidders = filteredBidders.length;

  // Group users by user_type (in this case, only bidders)
  const groupedUsers = useMemo(() => {
    return filteredBidders.reduce((acc, user) => {
      if (!acc[user.user_type]) {
        acc[user.user_type] = [];
      }
      acc[user.user_type].push(user);
      return acc;
    }, {} as Record<string, any[]>);
  }, [filteredBidders]);

  // User type configuration
  const userTypeConfig: Record<string, UserTypeConfig> = {
    bidder: {
      label: "Bidders",
      icon: UsersIcon,
      color: "bg-green-50 border-green-200 text-green-700",
      iconColor: "text-green-600",
      badgeColor: "bg-green-100 text-green-800",
      bgColor: "bg-green-500",
    },
  };

  // Get config for user type with fallback
  const getConfigForUserType = (userType: string): UserTypeConfig => {
    return (
      userTypeConfig[userType] || {
        label: userType.charAt(0).toUpperCase() + userType.slice(1),
        icon: UsersIcon,
        color: "bg-gray-50 border-gray-200 text-gray-700",
        iconColor: "text-green-600",
        badgeColor: "bg-green-100 text-green-800",
        bgColor: "bg-green-500",
      }
    );
  };

  // Generate user avatar initials
  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Format session time
  const formatSessionTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Toggle group expansion
  const toggleGroup = (userType: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [userType]: !prev[userType],
    }));
  };

  if (totalBidders === 0) {
    return (
      <div className={`fixed ${position}-4 bottom-4 z-50`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
        >
          <UsersIcon className="h-6 w-6 text-gray-600" />
        </button>

        {isOpen && (
          <div
            className={`absolute ${
              position === "right" ? "right-0" : "left-0"
            } bottom-16 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden`}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">
                Users History
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <XIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 text-center text-gray-500">
              <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm">No bidders found</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`fixed ${position}-4 bottom-4 z-50`}>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 ${
          isOpen ? "bg-blue-50 border-blue-300" : ""
        }`}
      >
        <UsersIcon className="h-6 w-6 text-gray-600" />
      </button>

      {/* Floating Panel */}
      {isOpen && (
        <div
          className={`absolute ${
            position === "right" ? "right-0" : "left-0"
          } bottom-16 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden`}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <UsersIcon className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Users History
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <XIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Compact View with Accordion */}
          <div className="max-h-96 overflow-y-auto">
            {Object.entries(groupedUsers).map(([userType, users]) => {
              const config = getConfigForUserType(userType);
              const IconComponent = config.icon;
              const isExpanded = expandedGroups[userType];

              return (
                <div
                  key={userType}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {/* Group Header - Clickable for accordion */}
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleGroup(userType)}
                  >
                    <div className="flex items-center space-x-2">
                      <IconComponent
                        className={`h-4 w-4 ${config.iconColor}`}
                      />
                      <span className="text-sm font-medium">
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${config.badgeColor}`}
                      >
                        {users.length}
                      </span>
                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded User List */}
                  {isExpanded && (
                    <div className="px-3 pb-3 bg-gray-50">
                      <div className="space-y-2">
                        {users.map((user) => (
                          <div
                            key={user.id}
                            className="p-3 bg-white rounded border border-gray-200"
                          >
                            <div
                              key={user.id}
                              className="flex items-start sm:items-center justify-between gap-2 min-w-0"
                            >
                              <div className="flex items-center space-x-2 min-w-0 flex-1 overflow-hidden">
                                <div
                                  className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center text-white text-sm font-medium flex-shrink-0`}
                                >
                                  {getUserInitials(user.name)}
                                </div>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <div className="uppercase text-sm font-medium text-gray-900 truncate">
                                    {user.name}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {user.email}
                                  </div>
                                </div>
                              </div>

                              {/* Session Details */}
                              <div className="text-xs text-gray-600 flex-shrink-0">
                                <div className="flex items-center whitespace-nowrap">
                                  <ClockIcon className="h-3 w-3 mr-1" />
                                  {formatSessionTime(user.sessionDuration)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingUserHistory;
